import React, { useEffect, useState } from 'react';
import { Switch, TimePicker, Button, Typography, Spin, Calendar, Modal, Select, Input, Popconfirm, theme } from 'antd';
import { message } from '../../services/toastMessage';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { IconMoodSad } from '@tabler/icons-react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import AppLayout from '../../components/Layouts/Layout';
import BackButton from '../../components/BackButton';
import EmptyState from '../../components/EmptyState';
import { getHeavyDutyMachineryById } from '../../services/HeavyDutyMachineryService';
import {
  getSchedule, updateSchedule, getExceptions, createException, updateException, deleteException,
} from '../../services/AvailabilityService';
import { IWeeklyScheduleDay, ITimeSlot, IAvailabilityException, AvailabilityExceptionType } from '../../interfaces/heavyDutyMachinery/IAvailability';
import { IHeavyDutyMachinery } from '../../interfaces/heavyDutyMachinery/IHeavyDutyMachinery';
import { CORAL, pillToneStyle } from '../../theme/creamyTokens';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';

const { Title, Text } = Typography;
const TIME_FORMAT = 'HH:mm';

const buildDefaultWeek = (): IWeeklyScheduleDay[] =>
  Array.from({ length: 7 }, (_, dayOfWeek) => ({ dayOfWeek, isClosed: true, isAvailable24h: false, slots: [] }));

const emptySlot = (): ITimeSlot => ({ startTime: '09:00', endTime: '18:00' });

// Dot colors for the exceptions calendar's day markers -- kept as a plain
// hex map (not pillToneStyle's semantic tones) since these three need to
// stay visually distinct from each other regardless of theme, the same way
// the modal's own type Select options do.
const EXCEPTION_TYPE_DOT_COLORS: Record<AvailabilityExceptionType, string> = {
  FullyBlocked: '#D4380D',
  CustomHours: '#D48806',
  ExtraAvailability: '#389E0D',
};

type AvailabilityView = 'schedule' | 'calendar';

/** Same pill-tab pattern as IncomingBookings' HostViewTabs, reused here for consistency across the app's host screens. */
const AvailabilityTabs: React.FC<{ value: AvailabilityView; onChange: (value: AvailabilityView) => void }> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const options: { value: AvailabilityView; labelKey: string }[] = [
    { value: 'schedule', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_TAB_WEEKLY_SCHEDULE },
    { value: 'calendar', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_TAB_CALENDAR },
  ];
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            style={{
              border: 'none',
              borderRadius: 999,
              padding: '8px 16px',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              background: active ? `${CORAL}1A` : 'transparent',
              color: active ? CORAL : token.colorTextSecondary,
              transition: 'background-color 150ms ease, color 150ms ease',
            }}
          >
            {translate(t, option.labelKey)}
          </button>
        );
      })}
    </div>
  );
};

const HeavyDutyMachineryAvailability: React.FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { id } = useParams<{ id: string }>();

  const [space, setSpace] = useState<IHeavyDutyMachinery | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<AvailabilityView>('schedule');

  const [week, setWeek] = useState<IWeeklyScheduleDay[]>(buildDefaultWeek());
  const [savingSchedule, setSavingSchedule] = useState(false);

  const [month, setMonth] = useState<Dayjs>(dayjs());
  const [exceptions, setExceptions] = useState<IAvailabilityException[]>([]);
  const [loadingExceptions, setLoadingExceptions] = useState(false);

  const [modalDate, setModalDate] = useState<Dayjs | undefined>(undefined);
  const [modalType, setModalType] = useState<AvailabilityExceptionType>('FullyBlocked');
  const [modalReason, setModalReason] = useState('');
  const [modalSlots, setModalSlots] = useState<ITimeSlot[]>([]);
  const [modalSaving, setModalSaving] = useState(false);

  const calendarMinYear = dayjs().year() - 1;
  const calendarMaxYear = dayjs().year() + 2;

  const dayLabelKeys = [
    TRANSLATIONS.HEAVY_DUTY_MACHINERY_DAY_SUNDAY,
    TRANSLATIONS.HEAVY_DUTY_MACHINERY_DAY_MONDAY,
    TRANSLATIONS.HEAVY_DUTY_MACHINERY_DAY_TUESDAY,
    TRANSLATIONS.HEAVY_DUTY_MACHINERY_DAY_WEDNESDAY,
    TRANSLATIONS.HEAVY_DUTY_MACHINERY_DAY_THURSDAY,
    TRANSLATIONS.HEAVY_DUTY_MACHINERY_DAY_FRIDAY,
    TRANSLATIONS.HEAVY_DUTY_MACHINERY_DAY_SATURDAY,
  ];

  const exceptionTypeLabelKeys: Record<AvailabilityExceptionType, string> = {
    FullyBlocked: TRANSLATIONS.HEAVY_DUTY_MACHINERY_EXCEPTION_TYPE_FULLY_BLOCKED,
    CustomHours: TRANSLATIONS.HEAVY_DUTY_MACHINERY_EXCEPTION_TYPE_CUSTOM_HOURS,
    ExtraAvailability: TRANSLATIONS.HEAVY_DUTY_MACHINERY_EXCEPTION_TYPE_EXTRA_AVAILABILITY,
  };

  useEffect(() => {
    if (!id) return;
    Promise.all([getHeavyDutyMachineryById(id), getSchedule(id)])
      .then(([spaceData, scheduleData]) => {
        setSpace(spaceData);
        setWeek(buildDefaultWeek().map((d) => scheduleData.find((s) => s.dayOfWeek === d.dayOfWeek) || d));
      })
      .catch(() => message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_LOAD_ERROR)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchExceptions = (targetMonth: Dayjs) => {
    if (!id) return;
    setLoadingExceptions(true);
    const from = targetMonth.startOf('month').subtract(7, 'day').format('YYYY-MM-DD');
    const to = targetMonth.endOf('month').add(7, 'day').format('YYYY-MM-DD');
    getExceptions(id, from, to)
      .then(setExceptions)
      .catch(() => message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_AVAILABILITY_LOAD_ERROR)))
      .finally(() => setLoadingExceptions(false));
  };

  useEffect(() => {
    fetchExceptions(month);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, month]);

  const updateDay = (dayOfWeek: number, patch: Partial<IWeeklyScheduleDay>) => {
    setWeek((prev) => prev.map((d) => (d.dayOfWeek === dayOfWeek ? { ...d, ...patch } : d)));
  };

  const addSlot = (dayOfWeek: number) => {
    const day = week.find((d) => d.dayOfWeek === dayOfWeek);
    updateDay(dayOfWeek, { slots: [...(day?.slots ?? []), emptySlot()] });
  };

  const removeSlot = (dayOfWeek: number, index: number) => {
    const day = week.find((d) => d.dayOfWeek === dayOfWeek);
    if (!day) return;
    updateDay(dayOfWeek, { slots: day.slots.filter((_, i) => i !== index) });
  };

  const updateSlot = (dayOfWeek: number, index: number, patch: Partial<ITimeSlot>) => {
    const day = week.find((d) => d.dayOfWeek === dayOfWeek);
    if (!day) return;
    updateDay(dayOfWeek, { slots: day.slots.map((s, i) => (i === index ? { ...s, ...patch } : s)) });
  };

  const handleSaveSchedule = async () => {
    if (!id) return;
    setSavingSchedule(true);
    try {
      const saved = await updateSchedule(id, week);
      setWeek(buildDefaultWeek().map((d) => saved.find((s) => s.dayOfWeek === d.dayOfWeek) || d));
      message.success(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SCHEDULE_SAVED));
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SCHEDULE_SAVE_ERROR));
    } finally {
      setSavingSchedule(false);
    }
  };

  // A single, flat rounded row per day -- deliberately not a Card-in-Card:
  // one level of framing (this row itself) is enough, nesting another Card
  // around the whole week on top of it just doubled the boxing for no
  // reason.
  const renderDayRow = (day: IWeeklyScheduleDay) => (
    <div
      key={day.dayOfWeek}
      style={{
        borderRadius: 18,
        border: `1px solid ${token.colorBorderSecondary}`,
        background: token.colorBgContainer,
        padding: 16,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <Text strong>{translate(t, dayLabelKeys[day.dayOfWeek])}</Text>
        <div style={{ display: 'flex', gap: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SCHEDULE_CLOSED_LABEL)}
            </Text>
            <Switch
              size="small"
              checked={day.isClosed}
              onChange={(checked) =>
                updateDay(day.dayOfWeek, { isClosed: checked, ...(checked ? { isAvailable24h: false } : {}) })
              }
            />
          </div>
          {!day.isClosed && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SCHEDULE_24H_LABEL)}
              </Text>
              <Switch
                size="small"
                checked={day.isAvailable24h}
                onChange={(checked) => updateDay(day.dayOfWeek, { isAvailable24h: checked })}
              />
            </div>
          )}
        </div>
      </div>

      {!day.isClosed && !day.isAvailable24h && (
        <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {day.slots.map((slot, index) => (
            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <TimePicker
                format={TIME_FORMAT}
                value={slot.startTime ? dayjs(slot.startTime, TIME_FORMAT) : undefined}
                onChange={(value) =>
                  updateSlot(day.dayOfWeek, index, { startTime: value ? value.format(TIME_FORMAT) : '' })
                }
              />
              <Text type="secondary">–</Text>
              <TimePicker
                format={TIME_FORMAT}
                value={slot.endTime ? dayjs(slot.endTime, TIME_FORMAT) : undefined}
                onChange={(value) =>
                  updateSlot(day.dayOfWeek, index, { endTime: value ? value.format(TIME_FORMAT) : '' })
                }
              />
              <Button
                shape="circle"
                size="small"
                style={{ border: 'none', ...pillToneStyle('error', token) }}
                icon={<DeleteOutlined />}
                onClick={() => removeSlot(day.dayOfWeek, index)}
              />
            </div>
          ))}
          <Button
            shape="round"
            style={{ border: 'none', alignSelf: 'flex-start', ...pillToneStyle('default', token) }}
            icon={<PlusOutlined />}
            onClick={() => addSlot(day.dayOfWeek)}
          >
            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SCHEDULE_ADD_SLOT)}
          </Button>
        </div>
      )}
    </div>
  );

  const renderWeeklySchedule = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {week.map(renderDayRow)}
      <Button
        type="primary"
        shape="round"
        block
        style={{ height: 48, marginTop: 4 }}
        loading={savingSchedule}
        onClick={handleSaveSchedule}
      >
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SCHEDULE_SAVE)}
      </Button>
    </div>
  );

  const findException = (date: Dayjs) => exceptions.find((e) => e.date === date.format('YYYY-MM-DD'));

  const openModalForDate = (date: Dayjs) => {
    const existing = findException(date);
    setModalDate(date);
    setModalType(existing?.type ?? 'FullyBlocked');
    setModalReason(existing?.reason ?? '');
    setModalSlots(existing?.slots ?? []);
  };

  const closeModal = () => setModalDate(undefined);

  const handleSaveException = async () => {
    if (!id || !modalDate) return;
    setModalSaving(true);
    const dateStr = modalDate.format('YYYY-MM-DD');
    const existing = findException(modalDate);
    const slots = modalType === 'FullyBlocked' ? [] : modalSlots;
    try {
      if (existing) {
        await updateException(id, existing.id, { type: modalType, reason: modalReason || undefined, slots });
      } else {
        await createException(id, { date: dateStr, type: modalType, reason: modalReason || undefined, slots });
      }
      message.success(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_EXCEPTION_SAVED));
      closeModal();
      fetchExceptions(month);
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_EXCEPTION_SAVE_ERROR));
    } finally {
      setModalSaving(false);
    }
  };

  const handleDeleteException = async () => {
    if (!id || !modalDate) return;
    const existing = findException(modalDate);
    if (!existing) return;
    setModalSaving(true);
    try {
      await deleteException(id, existing.id);
      message.success(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_EXCEPTION_DELETED));
      closeModal();
      fetchExceptions(month);
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_EXCEPTION_DELETE_ERROR));
    } finally {
      setModalSaving(false);
    }
  };

  // Same reasoning as IncomingBookings' own calendar tab: antd's default
  // header includes a Year view, which calls fullCellRender once per MONTH
  // instead of once per day -- only ever offering our own year/month
  // dropdowns keeps the Calendar's body from entering that view at all.
  const calendarCellRender = (date: Dayjs) => {
    const exception = findException(date);
    return (
      <div style={{ position: 'relative', height: '100%', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span>{date.date()}</span>
        {exception && (
          <span
            style={{
              position: 'absolute',
              bottom: 2,
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: EXCEPTION_TYPE_DOT_COLORS[exception.type],
            }}
          />
        )}
      </div>
    );
  };

  const modalExisting = modalDate ? findException(modalDate) : undefined;

  const renderCalendar = () => (
    <div>
      {loadingExceptions && <Spin style={{ marginBottom: 16 }} />}
      <Calendar
        fullscreen={false}
        value={month}
        onPanelChange={(value) => setMonth(value)}
        onSelect={(date, info) => {
          if (info.source === 'date') openModalForDate(date);
        }}
        fullCellRender={(date) => calendarCellRender(date)}
        headerRender={({ value, onChange }) => (
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <Select
              size="small"
              value={value.year()}
              style={{ width: 90 }}
              onChange={(y) => onChange(value.clone().year(y))}
              options={Array.from({ length: calendarMaxYear - calendarMinYear + 1 }, (_, i) => calendarMinYear + i).map((y) => ({
                value: y,
                label: y,
              }))}
            />
            <Select
              size="small"
              value={value.month()}
              style={{ width: 90 }}
              onChange={(m) => onChange(value.clone().month(m))}
              options={Array.from({ length: 12 }, (_, m) => ({ value: m, label: dayjs().month(m).format('MMM') }))}
            />
          </div>
        )}
      />

      <div style={{ display: 'flex', gap: 16, marginTop: 4, flexWrap: 'wrap' }}>
        {(Object.keys(EXCEPTION_TYPE_DOT_COLORS) as AvailabilityExceptionType[]).map((exceptionType) => (
          <div key={exceptionType} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: EXCEPTION_TYPE_DOT_COLORS[exceptionType] }} />
            <Text type="secondary" style={{ fontSize: 13 }}>
              {translate(t, exceptionTypeLabelKeys[exceptionType])}
            </Text>
          </div>
        ))}
      </div>

      <Modal
        title={modalDate ? modalDate.format('DD/MM/YYYY') : ''}
        open={!!modalDate}
        onCancel={closeModal}
        styles={{
          content: {
            borderRadius: 20,
            border: '0.5px solid rgba(0,0,0,0.08)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
          },
        }}
        footer={[
          modalExisting && (
            <Popconfirm
              key="delete"
              title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_EXCEPTION_DELETE_CONFIRM)}
              onConfirm={handleDeleteException}
            >
              <Button danger shape="round" loading={modalSaving}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_EXCEPTION_DELETE)}
              </Button>
            </Popconfirm>
          ),
          <Button key="cancel" shape="round" onClick={closeModal}>
            {translate(t, TRANSLATIONS.CANCEL)}
          </Button>,
          <Button key="save" type="primary" shape="round" loading={modalSaving} onClick={handleSaveException}>
            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_EXCEPTION_SAVE)}
          </Button>,
        ]}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_EXCEPTION_TYPE_LABEL)}
            </Text>
            <Select
              style={{ width: '100%' }}
              value={modalType}
              onChange={(value) => setModalType(value)}
              options={[
                { value: 'FullyBlocked', label: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_EXCEPTION_TYPE_FULLY_BLOCKED) },
                { value: 'CustomHours', label: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_EXCEPTION_TYPE_CUSTOM_HOURS) },
                { value: 'ExtraAvailability', label: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_EXCEPTION_TYPE_EXTRA_AVAILABILITY) },
              ]}
            />
          </div>
          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_EXCEPTION_REASON_LABEL)}
            </Text>
            <Input value={modalReason} onChange={(e) => setModalReason(e.target.value)} maxLength={200} />
          </div>
          {modalType !== 'FullyBlocked' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_EXCEPTION_SLOTS_LABEL)}
              </Text>
              {modalSlots.map((slot, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <TimePicker
                    format={TIME_FORMAT}
                    value={slot.startTime ? dayjs(slot.startTime, TIME_FORMAT) : undefined}
                    onChange={(value) =>
                      setModalSlots((prev) =>
                        prev.map((s, i) => (i === index ? { ...s, startTime: value ? value.format(TIME_FORMAT) : '' } : s))
                      )
                    }
                  />
                  <Text type="secondary">–</Text>
                  <TimePicker
                    format={TIME_FORMAT}
                    value={slot.endTime ? dayjs(slot.endTime, TIME_FORMAT) : undefined}
                    onChange={(value) =>
                      setModalSlots((prev) =>
                        prev.map((s, i) => (i === index ? { ...s, endTime: value ? value.format(TIME_FORMAT) : '' } : s))
                      )
                    }
                  />
                  <Button
                    shape="circle"
                    size="small"
                    style={{ border: 'none', ...pillToneStyle('error', token) }}
                    icon={<DeleteOutlined />}
                    onClick={() => setModalSlots((prev) => prev.filter((_, i) => i !== index))}
                  />
                </div>
              ))}
              <Button
                shape="round"
                style={{ border: 'none', alignSelf: 'flex-start', ...pillToneStyle('default', token) }}
                icon={<PlusOutlined />}
                onClick={() => setModalSlots((prev) => [...prev, emptySlot()])}
              >
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SCHEDULE_ADD_SLOT)}
              </Button>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );

  if (loading) {
    return (
      <AppLayout>
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      </AppLayout>
    );
  }

  if (!space) {
    return (
      <AppLayout>
        <EmptyState
          icon={<IconMoodSad size={24} />}
          title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_NOT_FOUND)}
          description={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_NOT_FOUND_DESCRIPTION)}
        />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div style={{ width: '100%', maxWidth: 1000 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <BackButton fallbackPath="/heavy-duty-machinery/mine" />
          <Title level={3} style={{ margin: 0 }}>
            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_AVAILABILITY_TITLE)}
          </Title>
        </div>
        <Text type="secondary" style={{ display: 'block', marginBottom: 20 }}>
          {space.title}
        </Text>

        <>
          <AvailabilityTabs value={view} onChange={setView} />
          {view === 'schedule' ? renderWeeklySchedule() : renderCalendar()}
        </>
      </div>
    </AppLayout>
  );
};

export default HeavyDutyMachineryAvailability;
